import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useEffect, useState } from 'react'
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { routes } from 'constants/routes'
import { setUser, removeUser } from 'store/slices/UserSlice'
import { getCurrentUser } from 'service/AuthService'
import { clearSession, setApiStatusHandler } from 'http/client'
import { getHomePath, hasAccess, canAccessAdmin, canAccessRequests, isOperational } from 'utils/access'
import NoMatch from 'pages/NoMatch/NoMatch'
import WorkspacePage from 'pages/WorkspacePage/WorkspacePage'
import { ShieldCheck, RefreshCw, WifiOff } from 'lucide-react'

function RouteGate({ route, user }) {
  useInterfaceLanguage();
  if (route.public) return route.path === '/' && user.userId ? <Navigate to={getHomePath(user)} replace /> : route.component
  if (!user.userId) return <Navigate to="/" replace />
  const allowed = !route.access
    || (route.access === 'admin' && canAccessAdmin(user))
    || (route.access === 'requests' && canAccessRequests(user))
    || (route.access === 'reports' && hasAccess(user, 'reports'))
    || (route.access === 'drivers_edit' && hasAccess(user, 'drivers_edit'))
    || (route.access === 'users' && hasAccess(user, 'users'))
    || (route.access === 'operational' && (isOperational(user) || hasAccess(user, 'loads')))
    || (route.access === 'legacy' && (user.is_personal_kyrgyzkomur || ['employee', 'superuser'].includes(user.erp_role) || user.is_superuser))
  return allowed ? route.component : <Navigate to={getHomePath(user)} replace />
}
export default function App() {
  useInterfaceLanguage();
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(state => state.user)
  const [status, setStatus] = useState('checking')
  const [retry, setRetry] = useState(0)
  const [apiUnavailable, setApiUnavailable] = useState(false)
  useEffect(() => { setApiStatusHandler(setApiUnavailable); return () => setApiStatusHandler(null) }, [])
  useEffect(() => {
    let cancelled = false
    const bootstrap = async () => {
      const token = localStorage.getItem('token')
      const persist = localStorage.getItem('rememberMe') === 'true' || sessionStorage.getItem('erp-session') === 'true'
      if (!token || !persist) { clearSession(); dispatch(removeUser()); if (!cancelled) setStatus('ready'); return }
      try {
        const { data } = await getCurrentUser()
        if (cancelled) return
        if (data.erp_authenticated === false) { clearSession(); dispatch(removeUser()); setStatus('ready'); return }
        const profile = { ...data, token: localStorage.getItem('token') }
        dispatch(setUser(profile)); localStorage.setItem('user', JSON.stringify(profile)); setStatus('ready')
      } catch (error) {
        if (cancelled) return
        if ([401, 403].includes(error.response?.status)) { clearSession(); dispatch(removeUser()); setStatus('ready') }
        else setStatus('offline')
      }
    }
    bootstrap()
    return () => { cancelled = true }
  }, [dispatch, retry])
  useEffect(() => {
    const expired = () => { dispatch(removeUser()); setStatus('ready'); navigate('/', { replace: true }) }
    window.addEventListener('erp-session-expired', expired)
    return () => window.removeEventListener('erp-session-expired', expired)
  }, [dispatch, navigate])
  if (status !== 'ready') return <div className="erp-auth" style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--erp-bg)' , padding: 24 }}>
    <div style={{ textAlign: 'center', maxWidth: 360 }}><ShieldCheck size={28} color="#0d00f3" /><h1 style={{ fontSize: 18, margin: '15px 0 8px' }}>{status === 'checking' ? tr("Загрузка рабочего пространства") : tr("Не удалось загрузить профиль")}</h1><p style={{ color: 'var(--erp-muted)' , fontSize: 13 }}>{status === 'checking' ? tr("Проверяем сессию и доступные разделы…") : tr("Проверьте соединение и повторите попытку.")}</p>{tr(status === 'offline' && <button style={{ padding: '10px 16px', border: '1px solid var(--erp-border)' , borderRadius: 7, background: 'var(--erp-surface)' , marginTop: 16 }} onClick={() => { setStatus('checking'); setRetry(value => value + 1) }}><RefreshCw size={13} />{tr(" Повторить")}</button>)}</div></div>
  return <div className="app">{tr(apiUnavailable && <div role="alert" style={{ padding: '10px 20px', background: 'var(--theme-warning-soft, #fbf4e7)' , color: 'var(--theme-warning-text, #9b7129)' , fontSize: 12, position: 'relative', zIndex: 150 }}><WifiOff size={14} style={{ verticalAlign: 'middle', marginRight: 7 }} />{tr("Нет связи с сервером. Проверьте соединение и повторите действие.")}</div>)}<Routes>{routes.map(route => <Route key={route.path} path={route.path} element={<RouteGate route={route} user={user} />} />)}
    <Route path="/workspace/" element={user.userId ? <WorkspacePage /> : <Navigate to="/" replace />} />
    <Route path="/profile/" element={user.userId ? <WorkspacePage profile /> : <Navigate to="/" replace />} />
    <Route path="/home/*" element={<Navigate to={user.userId ? getHomePath(user) : '/'} replace />} />
    <Route path="*" element={<NoMatch />} /></Routes></div>
}
